import React, { Suspense } from 'react';
import { HashRouter as Router, Route, Switch } from 'react-router-dom';
import { DummyPageShimmer } from 'components/molecules/Shimmer';
import BusinessDetailsChargesShimmer from 'components/pages/V2-order-flow/BusinessDetailsChargesPage/Shimmer';

const OrderDetailsContainer = React.lazy(() => import('containers/OrderDetailsContainer'));
const PlanDetailsContainer = React.lazy(() => import('containers/PlanDetailsContainer'));
const ApplicationBusinessDetailsContainer = React.lazy(() => import('containers/ApplicationBusinessDetailsContainer'));
const ApplicationLandingContainer = React.lazy(() => import('containers/ApplicationLandingContainer'));
const BusinessAddressFormContainer = React.lazy(() => import('containers/BusinessAddressFormContainer'));
const BusinessDetailsFormContainer = React.lazy(() => import('containers/BusinessDetailsFormContainer'));
const BharatSwipeReturnContainer = React.lazy(() => import('containers/BharatSwipeReturnContainer'));
const BharatSwipeReturnReasonContainer = React.lazy(() => import('containers/BharatSwipeReturnReasonContainer'));
const OrderPaperRollContainer = React.lazy(() => import('containers/OrderPaperRollContainer'));
const OrderPaperRollAddressContainer = React.lazy(() => import('containers/OrderPaperRollAddressContainer'));
const TransactionsListContainer = React.lazy(() => import('containers/TransactionsListContainer'));
const Statement = React.lazy(() => import('containers/StatementContainer'));
const SwipeTransactionDetail = React.lazy(() => import('containers/SwipeTransactionContainer'));
const ProductPricingContainer = React.lazy(() => import('containers/ProductPricingContainer'));
const DisputedTransactions = React.lazy(() => import('containers/DisputedTransactions'));
const UploadChargebackInvoiceContainer = React.lazy(() => import('containers/UploadChargebackInvoiceContainer'));
const DisputedTransactionDeclaration = React.lazy(() => import('containers/DisputedTransactionDeclaration'));
const ReturnDeviceListingContainer = React.lazy(() => import('containers/ReturnDeviceListingContainer'));
const ReturnStatusContainer = React.lazy(() => import('containers/ReturnStatusContainer'));
const DeactivateStatusContainer = React.lazy(() => import('containers/DeactivateStatusContainer'));
const ReturnDetailsContainer = React.lazy(() => import('containers/ReturnDetailsContainer'));
const ReturnRequestDetailsContainer = React.lazy(() => import('containers/ReturnRequestDetailsContainer'));
const OverdueTransactionsList = React.lazy(() => import('containers/OverdueTransactionsList'));
const OverdueTxnDetailsContainer = React.lazy(() => import('containers/OverdueTxnDetailsContainer'));
const MigrationContainer = React.lazy(() => import('containers/MigrationContainer'));
const DevicePlanListingContainer = React.lazy(() => import('containers/DevicePlanListingContainer'));
const DeviceComparisonContainer = React.lazy(() => import('containers/DeviceComparisonContainer'));
const DeviceConfirmChargesContainer = React.lazy(() => import('containers/DeviceConfirmChargesContainer'));
const MyChargesContainer = React.lazy(() => import('containers/MyChargesContainer'));
const MyChargesDeviceSelectionContainer = React.lazy(() => import('containers/MyChargesDeviceSelectionContainer'));
const OldPlansPricingContainer = React.lazy(() => import('containers/OldPlansPricingContainer'));
const ConfirmPurchaseDetailsContainer = React.lazy(() => import('containers/ConfirmPurchaseDetailsContainer'));
const RentalCashbackBreakdown = React.lazy(() => import('containers/RentalCashbackBreakdown'));
const MigrationLandingContainer = React.lazy(() => import('containers/MigrationLandingContainer'));
const PendingMigrationLandingContainer = React.lazy(() => import('containers/PendingMigrationLandingContainer'));
const MigrationSelectRankingConatiner = React.lazy(() => import('containers/MigrationSelectRankingConatiner'));
const CancellationRefundStatusContainer = React.lazy(() => import('containers/CancellationRefundStatusContainer'));
const UpdateMerchantRankingContainer = React.lazy(() => import('containers/UpdateMerchantRankingContainer'));
const ServiceRequestContainer = React.lazy(() => import('containers/ServiceRequestContainer'));
const ServiceRequestIssueDetailContainer = React.lazy(() => import('containers/ServiceRequestIssueDetailContainer'));
const SwipeEmiStepsContainer = React.lazy(() => import('containers/SwipeEmiStepsContainer'));
const CashbackDetails = React.lazy(() => import('containers/YearlyCashbackDetails/YearlyCashbackDetails'));
const ReferAndEarnContainer = React.lazy(() => import('containers/ReferAndEarnContainer'));
const BharatpeOnePlanContainer = React.lazy(() => import('containers/BharatpeOnePlanContainer'));
const ServiceRequestListingContainer = React.lazy(() => import('containers/ServiceRequestListingContainer'));
const ServiceRequestReasonContainer = React.lazy(() => import('containers/ServiceRequestReasonContainer'));
const ServiceRequestStatusContainer = React.lazy(() => import('containers/ServiceRequestStatusContainer'));
const SwipeOrdersListingContainer = React.lazy(() => import('containers/SwipeOrdersListingContainer'));
const SwipeOrdersListingDetailsContainer = React.lazy(() => import('containers/SwipeOrdersListingDetailsContainer'));
const ExistingPOSDeviceInfoForm = React.lazy(() => import('containers/ExistingPOSDeviceInfoForm'));

// <--------------------- V2 Order Flow --------------------------------->
const ApplicationFlowLandingContainer = React.lazy(
	() => import('containers/V2-Order-Flow/ApplicationFlowLandingContainer'),
);
const SelectDeviceContainer = React.lazy(() => import('containers/V2-Order-Flow/SelectDeviceContainer'));
const UpgradeDeviceOfferContainer = React.lazy(() => import('containers/V2-Order-Flow/UpgradeDeviceOfferContainer'));
const SelectPlanContainer = React.lazy(() => import('containers/V2-Order-Flow/SelectPlanContainer'));
const BusinessDetailsChargesContainer = React.lazy(
	() => import('containers/V2-Order-Flow/BusinessDetailsChargesContainer'),
);
const SelectAddressContainer = React.lazy(() => import('containers/V2-Order-Flow/SelectAddressContainer'));
const ProcessKycContainer = React.lazy(() => import('containers/V2-Order-Flow/ProcessKycContainer'));
const ProcessInitiatePaymentContainer = React.lazy(
	() => import('containers/V2-Order-Flow/ProcessInitiatePaymentContainer'),
);
const VerifyKycDocsContainer = React.lazy(() => import('containers/V2-Order-Flow/VerifyKycDocsContainer'));
const CpvHoldPageContainer = React.lazy(() => import('containers/V2-Order-Flow/CpvHoldPageContainer'));

const Routes: React.FC = () => (
	<Router basename={process.env.PUBLIC_URL}>
		<Switch>
			<Route exact path="/">
				<Suspense fallback={<DummyPageShimmer />}>
					<ApplicationLandingContainer />
				</Suspense>
			</Route>
			<Route exact path="/order-details/:orderId">
				<Suspense fallback={<DummyPageShimmer />}>
					<OrderDetailsContainer />
				</Suspense>
			</Route>
			<Route exact path="/plan-details/:applicationId">
				<Suspense fallback={<DummyPageShimmer />}>
					<PlanDetailsContainer />
				</Suspense>
			</Route>
			<Route exact path="/business-details">
				<Suspense fallback={<DummyPageShimmer />}>
					<ApplicationBusinessDetailsContainer />
				</Suspense>
			</Route>
			<Route exact path="/business-address/:merchantType/:source">
				<Suspense fallback={<DummyPageShimmer />}>
					<BusinessAddressFormContainer />
				</Suspense>
			</Route>
			<Route exact path="/business-details-form/:addId/:from">
				<Suspense fallback={<DummyPageShimmer />}>
					<BusinessDetailsFormContainer />
				</Suspense>
			</Route>
			<Route exact path="/device-plan-listing">
				<Suspense fallback={<DummyPageShimmer />}>
					<DevicePlanListingContainer />
				</Suspense>
			</Route>
			<Route exact path="/product-pricing/:planId">
				<Suspense fallback={<DummyPageShimmer />}>
					<ProductPricingContainer />
				</Suspense>
			</Route>
			<Route exact path="/old-plans-pricing">
				<Suspense fallback={<DummyPageShimmer />}>
					<OldPlansPricingContainer />
				</Suspense>
			</Route>
			<Route exact path="/device-comparison">
				<Suspense fallback={<DummyPageShimmer />}>
					<DeviceComparisonContainer />
				</Suspense>
			</Route>
			<Route exact path="/device-confirm-charges/:planId/:planSelected">
				<Suspense fallback={<DummyPageShimmer />}>
					<DeviceConfirmChargesContainer />
				</Suspense>
			</Route>
			<Route exact path="/confirm-purchase-details/:planId/:planSelected/:applicationId/:isLowTpvSelected">
				<Suspense fallback={<DummyPageShimmer />}>
					<ConfirmPurchaseDetailsContainer />
				</Suspense>
			</Route>
			<Route exact path="/select-device-my-charges/:source">
				<Suspense fallback={<DummyPageShimmer />}>
					<MyChargesDeviceSelectionContainer />
				</Suspense>
			</Route>
			<Route exact path="/device-my-charges/:source/:deviceSerial/:activeDate">
				<Suspense fallback={<DummyPageShimmer />}>
					<MyChargesContainer />
				</Suspense>
			</Route>
			<Route exact path="/transactions-list">
				<Suspense fallback={<DummyPageShimmer />}>
					<TransactionsListContainer />
				</Suspense>
			</Route>
			<Route exact path="/statement/:statementSource">
				<Suspense fallback={<DummyPageShimmer />}>
					<Statement />
				</Suspense>
			</Route>
			<Route exact path="/transaction-details/:source/:tranId">
				<Suspense fallback={<DummyPageShimmer />}>
					<SwipeTransactionDetail />
				</Suspense>
			</Route>
			<Route exact path="/disputed-txn-list">
				<Suspense fallback={<DummyPageShimmer />}>
					<DisputedTransactions />
				</Suspense>
			</Route>
			<Route exact path="/upload-chargeback/:chargebackId">
				<Suspense fallback={<DummyPageShimmer />}>
					<UploadChargebackInvoiceContainer />
				</Suspense>
			</Route>
			<Route exact path="/disputed-txn-declaration/:chargebackId">
				<Suspense fallback={<DummyPageShimmer />}>
					<DisputedTransactionDeclaration />
				</Suspense>
			</Route>
			<Route exact path="/return/listing">
				<Suspense fallback={<DummyPageShimmer />}>
					<ReturnDeviceListingContainer />
				</Suspense>
			</Route>
			<Route exact path="/return/:isSingleDevice/reason/:deviceSerial/:deviceType">
				<Suspense fallback={<DummyPageShimmer />}>
					<BharatSwipeReturnReasonContainer />
				</Suspense>
			</Route>
			<Route exact path="/return/:firstVisit/status/:deviceSerial">
				<Suspense fallback={<DummyPageShimmer />}>
					<ReturnStatusContainer />
				</Suspense>
			</Route>
			<Route exact path="/deactivate/:firstVisit/status/:deviceSerial">
				<Suspense fallback={<DummyPageShimmer />}>
					<DeactivateStatusContainer />
				</Suspense>
			</Route>
			<Route exact path="/return/request-details/:deviceSerial">
				<Suspense fallback={<DummyPageShimmer />}>
					<ReturnRequestDetailsContainer />
				</Suspense>
			</Route>
			<Route exact path="/return/refund-details/:deviceSerial">
				<Suspense fallback={<DummyPageShimmer />}>
					<ReturnDetailsContainer />
				</Suspense>
			</Route>
			<Route exact path="/bharat-swipe-return">
				<Suspense fallback={<DummyPageShimmer />}>
					<BharatSwipeReturnContainer />
				</Suspense>
			</Route>
			<Route exact path="/swipe-migration">
				<Suspense fallback={<DummyPageShimmer />}>
					<MigrationContainer />
				</Suspense>
			</Route>
			<Route exact path="/swipe-migration-landing">
				<Suspense fallback={<DummyPageShimmer />}>
					<MigrationLandingContainer />
				</Suspense>
			</Route>
			<Route exact path="/swipe-migration-ranking">
				<Suspense fallback={<DummyPageShimmer />}>
					<MigrationSelectRankingConatiner />
				</Suspense>
			</Route>
			<Route exact path="/overdue-list">
				<Suspense fallback={<DummyPageShimmer />}>
					<OverdueTransactionsList />
				</Suspense>
			</Route>
			<Route exact path="/overdue-details">
				<Suspense fallback={<DummyPageShimmer />}>
					<OverdueTxnDetailsContainer />
				</Suspense>
			</Route>
			<Route exact path="/rental-cashback-details">
				<Suspense fallback={<DummyPageShimmer />}>
					<RentalCashbackBreakdown />
				</Suspense>
			</Route>
			<Route exact path="/cancellation-refund-status">
				<Suspense fallback={<DummyPageShimmer />}>
					<CancellationRefundStatusContainer />
				</Suspense>
			</Route>
			<Route exact path="/service-request">
				<Suspense fallback={<DummyPageShimmer />}>
					<ServiceRequestContainer />
				</Suspense>
			</Route>
			<Route exact path="/service-request-issue/:selectedIssue">
				<Suspense fallback={<DummyPageShimmer />}>
					<ServiceRequestIssueDetailContainer />
				</Suspense>
			</Route>
			<Route exact path="/emi-steps">
				<Suspense fallback={<DummyPageShimmer />}>
					<SwipeEmiStepsContainer />
				</Suspense>
			</Route>
			<Route exact path="/order-paper-roll">
				<Suspense fallback={<DummyPageShimmer />}>
					<OrderPaperRollContainer />
				</Suspense>
			</Route>
			<Route exact path="/order-paper-roll/address">
				<Suspense fallback={<DummyPageShimmer />}>
					<OrderPaperRollAddressContainer />
				</Suspense>
			</Route>
			<Route exact path="/update-ranking">
				<Suspense fallback={<DummyPageShimmer />}>
					<UpdateMerchantRankingContainer />
				</Suspense>
			</Route>
			<Route exact path="/lifetime-fee-cashback">
				<Suspense fallback={<DummyPageShimmer />}>
					<CashbackDetails />
				</Suspense>
			</Route>
			<Route exact path="/refer-earn">
				<Suspense fallback={<DummyPageShimmer />}>
					<ReferAndEarnContainer />
				</Suspense>
			</Route>
			<Route exact path="/bharatpe-one-plan">
				<Suspense fallback={<DummyPageShimmer />}>
					<BharatpeOnePlanContainer />
				</Suspense>
			</Route>
			<Route exact path="/service-request-listing">
				<Suspense fallback={<DummyPageShimmer />}>
					<ServiceRequestListingContainer />
				</Suspense>
			</Route>
			<Route exact path="/service-request/reason/:deviceSerial">
				<Suspense fallback={<DummyPageShimmer />}>
					<ServiceRequestReasonContainer />
				</Suspense>
			</Route>
			<Route exact path="/service-request/:firstVisit/status/:deviceSerial/:requestId">
				<Suspense fallback={<DummyPageShimmer />}>
					<ServiceRequestStatusContainer />
				</Suspense>
			</Route>
			<Route exact path="/swipe-orders-listing">
				<Suspense fallback={<DummyPageShimmer />}>
					<SwipeOrdersListingContainer />
				</Suspense>
			</Route>
			<Route exact path="/swipe-orders-details/:applicationId/:returnId">
				<Suspense fallback={<DummyPageShimmer />}>
					<SwipeOrdersListingDetailsContainer />
				</Suspense>
			</Route>

			{/* <--------------------- V2 Order Flow ---------------------------------> */}
			<Route exact path="/V2/landing">
				<Suspense fallback={<DummyPageShimmer />}>
					<ApplicationFlowLandingContainer />
				</Suspense>
			</Route>
			<Route exact path="/V2/upgrade-device-offer/:source">
				<Suspense fallback={<DummyPageShimmer />}>
					<UpgradeDeviceOfferContainer />
				</Suspense>
			</Route>
			<Route exact path="/V2/select-device/:source">
				<Suspense fallback={<DummyPageShimmer />}>
					<SelectDeviceContainer />
				</Suspense>
			</Route>
			<Route exact path="/V2/select-plan/:planId/:source/:deviceSelectionMode">
				<Suspense fallback={<DummyPageShimmer />}>
					<SelectPlanContainer />
				</Suspense>
			</Route>
			<Route exact path="/V2/business-details/:source">
				<Suspense fallback={<BusinessDetailsChargesShimmer />}>
					<BusinessDetailsChargesContainer />
				</Suspense>
			</Route>
			<Route exact path="/V2/select-address/:source">
				<Suspense fallback={<DummyPageShimmer />}>
					<SelectAddressContainer />
				</Suspense>
			</Route>
			<Route exact path="/V2/add-new-address/:source/:addId">
				<Suspense fallback={<DummyPageShimmer />}>
					<SelectAddressContainer />
				</Suspense>
			</Route>
			<Route exact path="/V2/process-kyc/:source">
				<Suspense fallback={<DummyPageShimmer />}>
					<ProcessKycContainer />
				</Suspense>
			</Route>
			<Route exact path="/V2/process-payment">
				<Suspense fallback={<DummyPageShimmer />}>
					<ProcessInitiatePaymentContainer />
				</Suspense>
			</Route>
			<Route exact path="/V2/verify-kyc-docs/:isUploadCheck/:source">
				<Suspense fallback={<DummyPageShimmer />}>
					<VerifyKycDocsContainer />
				</Suspense>
			</Route>
			<Route exact path="/V2/cpv-hold">
				<Suspense fallback={<DummyPageShimmer />}>
					<CpvHoldPageContainer />
				</Suspense>
			</Route>
			<Route exact path="/V2/existing-pos-device">
				<Suspense fallback={<DummyPageShimmer />}>
					<ExistingPOSDeviceInfoForm />
				</Suspense>
			</Route>
			<Route exact path="/swipe-pending-migration-landing">
				<Suspense fallback={<DummyPageShimmer />}>
					<PendingMigrationLandingContainer />
				</Suspense>
			</Route>
		</Switch>
	</Router>
);

export default React.memo(Routes);
